import { io, Socket } from 'socket.io-client';

export interface DeviceUpdateData {
    mac: string;
    name: string;
    signal: any;
    lastSeen: Date;
    timestamp: Date;
    userId: string;
    position?: { x: number; y: number };
    rssi?: number;
    strongestGateway?: string;
    triangulationInfo?: any;
}

export interface SocketUpdateData {
    type: string;
    data: DeviceUpdateData;
    timestamp: Date;
}

class SocketService {
    private socket: Socket | null = null;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectDelay = 1000; // Start with 1 second
    private isConnecting = false;
    private apiUrl: string;

    constructor() {
        this.apiUrl = process.env.REACT_APP_SWITCHQ_WS_URL || 'http://localhost:3051';
    }

    /**
     * Connect to Socket.io server with authentication
     */
    connect(token?: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.isConnecting) {
                // console.log('🔌 Socket.io connection already in progress');
                return resolve();
            }

            if (this.socket?.connected) {
                // console.log('🔌 Socket.io already connected');
                return resolve();
            }

            this.isConnecting = true;

            // Get token from parameter or localStorage
            const authToken = token || localStorage.getItem('token');

            if (!authToken) {
                this.isConnecting = false;
                return reject(new Error('No authentication token available'));
            }

            // console.log('🔌 Connecting to Socket.io server...');
            // console.log('🔌 API URL:', this.apiUrl);
            // console.log('🔌 Token available:', !!authToken);

            // Connect to the full server URL with root namespace
            this.socket = io(this.apiUrl, {
                path: '/sqws',
                auth: {
                    token: authToken,
                },
                transports: ['websocket'], // Prefer WebSocket, fallback to polling
                // timeout: 10000,
                forceNew: true,
                autoConnect: true,
            });

            // console.log('🔌 Socket.io connecting to:', this.apiUrl);

            // Connection event handlers
            this.socket.on('connect', () => {
                // console.log(`🔌 Socket.io connected: ${this.socket?.id}`);
                this.isConnecting = false;
                this.reconnectAttempts = 0;
                this.reconnectDelay = 1000;
                resolve();
            });

            this.socket.on('disconnect', (reason) => {
                // console.log(`🔌 Socket.io disconnected: ${reason}`);
                this.isConnecting = false;

                if (reason === 'io server disconnect') {
                    // Server disconnected, try to reconnect
                    this.handleReconnect();
                }
            });

            this.socket.on('connect_error', (error) => {
                // console.error('🔌 Socket.io connection error:', error.message);
                this.isConnecting = false;

                if (error.message.includes('Authentication')) {
                    // Authentication failed, don't retry
                    reject(new Error('Authentication failed'));
                } else {
                    // Other connection error, try to reconnect
                    this.handleReconnect();
                }
            });

            // Device update handler
            this.socket.on('device_update', (data: SocketUpdateData) => {
                // console.log('📡 Received device update:', data.data.mac);
                // Emit custom event for components to listen to
                window.dispatchEvent(new CustomEvent('deviceUpdate', { detail: data }));
            });

            // Ping/pong for connection testing
            this.socket.on('pong', (data) => {
                console.log('🏓 Socket.io pong received:', data);
            });

            // Set a timeout for initial connection
            setTimeout(() => {
                if (this.isConnecting) {
                    this.isConnecting = false;
                    this.socket?.disconnect();
                    reject(new Error('Connection timeout'));
                }
            }, 10000);
        });
    }

    /**
     * Handle reconnection logic with exponential backoff
     */
    private handleReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            // console.error('🔌 Max reconnection attempts reached');
            return;
        }

        this.reconnectAttempts++;
        // console.log(`🔌 Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${this.reconnectDelay}ms`);

        setTimeout(() => {
            this.connect().catch((error) => {
                // console.error('🔌 Reconnection failed:', error);
            });
        }, this.reconnectDelay);

        // Exponential backoff
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000); // Max 30 seconds
    }

    /**
     * Disconnect from Socket.io server
     */
    disconnect() {
        if (this.socket) {
            // console.log('🔌 Disconnecting from Socket.io server');
            this.socket.disconnect();
            this.socket = null;
        }
        this.isConnecting = false;
        this.reconnectAttempts = 0;
    }

    /**
     * Send ping to test connection
     */
    ping(data: any = {}) {
        if (this.socket?.connected) {
            this.socket.emit('ping', data);
        } else {
            // console.warn('🔌 Cannot ping: Socket.io not connected');
        }
    }

    /**
     * Check if socket is connected
     */
    isConnected(): boolean {
        return this.socket?.connected || false;
    }

    /**
     * Get connection status
     */
    getStatus() {
        return {
            connected: this.isConnected(),
            connecting: this.isConnecting,
            reconnectAttempts: this.reconnectAttempts,
            socketId: this.socket?.id || null,
        };
    }

    /**
     * Update authentication token (for when user logs in/out)
     */
    updateToken(token: string) {
        if (this.socket) {
            // Disconnect and reconnect with new token
            this.disconnect();
            setTimeout(() => {
                this.connect(token).catch((error) => {
                    // console.error('🔌 Failed to reconnect with new token:', error);
                });
            }, 100);
        }
    }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService;
